import { Observable, from, of } from "rxjs";
import { catchError, map, switchMap, take } from "rxjs/operators";

import { AFTER_ONBOARDING_STATES, RouterStates } from "src/app/shared/consts";
import { RedirectAuthenticatedUsersAwayFromLandingPageExperimentKeyService } from "src/app/experiments/redirect-authenticated-users-away-from-landing-page/redirect-authenticated-users-away-from-landing-page.service";
import { User } from "src/app/shared/models/user.model";
import { AuthService } from "src/app/shared/services/auth.service";
import { StorageService } from "src/app/shared/services/storage.service";

import { getFirebaseUserAfterAuthRestore$ } from "./firebase-auth-restore";

const AUTH_STATE_TIMEOUT_MS = 5000;
export const AUTHENTICATED_LANDING_MAIN_REDIRECT_PATH = "/main";

const getStoredAuthenticatedUser$ = (
  storageService: StorageService
): Observable<User | null> =>
  from(storageService.get("user") as Promise<User | null>).pipe(
    take(1),
    map((user) => (user?.userId ? user : null)),
    catchError(() => of(null))
  );

export const shouldRedirectAuthenticatedLandingUser$ = (
  redirectAuthenticatedLandingUsersExperiment: RedirectAuthenticatedUsersAwayFromLandingPageExperimentKeyService,
  authService: AuthService,
  storageService: StorageService
): Observable<boolean> => {
  let redirectEnabledSnapshot: boolean | null;

  try {
    redirectEnabledSnapshot =
      redirectAuthenticatedLandingUsersExperiment.getRaw() as boolean | null;
  } catch {
    return of(false);
  }

  const redirectAuthenticatedUser$ = (waitForRestoredUser: boolean) =>
    getFirebaseUserAfterAuthRestore$(authService, AUTH_STATE_TIMEOUT_MS, {
      waitForRestoredUser,
    }).pipe(
      take(1),
      map((firebaseUser) => !!firebaseUser?.uid)
    );

  const redirectAuthenticatedUserWhenEnabled$ = (
    userId?: string,
    waitForRestoredUser = false
  ) => {
    const isRedirectEnabled$ = userId
      ? redirectAuthenticatedLandingUsersExperiment.isOnWhenReadyForAuthenticatedUser$(
          userId
        )
      : redirectAuthenticatedLandingUsersExperiment.isOnWhenReady$();

    return isRedirectEnabled$.pipe(
      take(1),
      switchMap((isRedirectEnabled) => {
        if (!isRedirectEnabled) {
          return of(false);
        }

        return redirectAuthenticatedUser$(waitForRestoredUser);
      }),
      catchError(() => of(false))
    );
  };

  if (redirectEnabledSnapshot === true) {
    return getStoredAuthenticatedUser$(storageService).pipe(
      switchMap((storedUser) =>
        redirectAuthenticatedUserWhenEnabled$(
          storedUser?.userId,
          Boolean(storedUser?.userId)
        )
      ),
      catchError(() => redirectAuthenticatedUserWhenEnabled$())
    );
  }

  if (redirectEnabledSnapshot === false) {
    return of(false);
  }

  return getStoredAuthenticatedUser$(storageService).pipe(
    switchMap((storedUser) => {
      if (!storedUser) {
        return of(false);
      }

      return redirectAuthenticatedUserWhenEnabled$(storedUser.userId, true);
    }),
    catchError(() => of(false))
  );
};

export const getAuthenticatedLandingRedirectPath = (
  user: User | null
): string => {
  if (!user?.userId) {
    return AUTHENTICATED_LANDING_MAIN_REDIRECT_PATH;
  }

  const userStatePage = user?.userState?.page;
  if (!userStatePage || AFTER_ONBOARDING_STATES.includes(userStatePage)) {
    return AUTHENTICATED_LANDING_MAIN_REDIRECT_PATH;
  }

  const redirectTo = RouterStates.get(userStatePage)?.redirectTo;
  return redirectTo
    ? `/${redirectTo}`
    : AUTHENTICATED_LANDING_MAIN_REDIRECT_PATH;
};
